import React, { useContext, useState } from "react";
import {
  CircularProgress,
  Button,
  Box,
  Dialog,
  DialogTitle,
  Backdrop,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  linkAttachmentToWorkOrder,
  getWorkOrderRequestDtoList,
  IWorkOrderMapObject,
  OcpwCreateWorkOrder,
  uploadAttachmentToAim,
} from "../../../../apis/OcpwAssetworks/OcpwAssetworksApi";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  PopupRequest,
  SilentRequest,
} from "@azure/msal-browser";
import { OCLandInsightsContext } from "../../../../hooks/context/OcLandInsightsContext";
import { updateFeaturesInGisWithWorkOrderResults } from "../updateGISWithAimData";
import WorkOrderPreviewDialog from "../WorkOrderPreview";
import WorkOrderDialogSuccessReport from "../WorkOrderDialogSuccessReport";
import RejectedWorkOrderDialogReport from "../RejectedWorkOrderDialogReport";
import { WorkOrderResponse } from "../WorkOrderDialogSuccessReport";
import {
  removeAllFromSelectionLayer,
  selectDataHandler,
} from "../../select-form/spatial-select/spatialSelectVM";

type CreateWorkOrderProps = {
  payload: any[];
  portal: any;
  view: any;
};

const recoverableMsalErrorCodes = [
  "monitor_window_timeout",
  "login_required",
  "consent_required",
  "interaction_required",
  "no_tokens_found",
  "token_refresh_required",
];

const getErrorMessage = (error: any, fallbackMessage: string) => {
  if (typeof error === "string") {
    return error;
  }

  if (error?.message) {
    return error.message;
  }

  if (error?.errorMessage) {
    return error.errorMessage;
  }

  return fallbackMessage;
};

const isRecoverableMsalError = (error: any) => {
  const errorCode = error?.errorCode;

  return (
    error instanceof InteractionRequiredAuthError ||
    recoverableMsalErrorCodes.includes(errorCode)
  );
};

const CreateWorkOrder = ({ payload = [], portal, view }: CreateWorkOrderProps) => {
  const { instance, accounts, inProgress } = useMsal();

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [dtoPreviewOpen, setDtoPreviewOpen] = useState(false);
  const [dtoPreview, setDtoPreview] = useState<IWorkOrderMapObject[][]>([]);
  const [dtoPreviewToken, setDtoPreviewToken] = useState<string | null>(null);

  const [workOrderSuccessDialogOpen, setWorkOrderSuccessDialogOpen] =
    useState(false);
  const [workOrderRejectedDialogOpen, setWorkOrderRejectedDialogOpen] =
    useState(false);
  const [createdWorkOrders, setCreatedWorkOrders] = useState<
    WorkOrderResponse[]
  >([]);
  const [rejectedWorkOrders, setRejectedWorkOrders] = useState<any[]>([]);

  const ocLandInsightsDictionaryCtx: any = useContext(OCLandInsightsContext);

  const getAccessToken = async (): Promise<string> => {
    const apiScope = process.env.REACT_APP_API_SCOPE;

    if (!apiScope) {
      throw new Error("Missing REACT_APP_API_SCOPE environment variable.");
    }

    const account = instance.getActiveAccount() || accounts[0];

    if (!account) {
      throw new Error("User is not logged in to a Microsoft Account.");
    }

    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(account);
    }

    const silentRequest: SilentRequest = {
      scopes: [apiScope],
      account,
    };

    try {
      const silentResponse = await instance.acquireTokenSilent(silentRequest);
      return silentResponse.accessToken;
    } catch (silentError: any) {
      console.error("Silent token acquisition failed:", silentError);

      if (!isRecoverableMsalError(silentError)) {
        throw silentError;
      }

      console.warn(
        "Silent token failed. Trying interactive Microsoft popup token request."
      );

      const popupRequest: PopupRequest = {
        scopes: [apiScope],
        account,
      };

      const popupResponse = await instance.acquireTokenPopup(popupRequest);

      if (popupResponse.account) {
        instance.setActiveAccount(popupResponse.account);
      }

      return popupResponse.accessToken;
    }
  };

  const onClick = async () => {
    setLoading(true);
    setError(null);

    if (inProgress !== InteractionStatus.None) {
      setLoading(false);
      setError(
        "Microsoft sign-in is still in progress. Please wait a few seconds and try again."
      );
      return;
    }

    try {
      const token = await getAccessToken();

      await handlePreviewDto(
        payload,
        token,
        portal,
        ocLandInsightsDictionaryCtx.data
      );
    } catch (tokenError: any) {
      console.error("Token acquisition failed:", tokenError);

      setError(
        getErrorMessage(
          tokenError,
          "Microsoft token acquisition failed. Please try signing in again."
        )
      );

      setLoading(false);
    }
  };

  const handlePreviewDto = async (
    payload,
    token,
    portal,
    ocLandInsightsIdTable
  ) => {
    try {
      const allDtos: IWorkOrderMapObject[][] = [];

      for (const pay of payload) {
        const dtoMap: IWorkOrderMapObject[] = await getWorkOrderRequestDtoList({
          data: pay,
          portal,
          token,
          ocLandInsightsIdTable,
        });

        allDtos.push(dtoMap);
      }

      setDtoPreview(allDtos);
      setDtoPreviewToken(token);
      setDtoPreviewOpen(true);
    } catch (previewError: any) {
      setError(
        getErrorMessage(
          previewError,
          "Failed to generate work order preview."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkOrder = async () => {
    setDtoPreviewOpen(false);
    setLoading(true);

    if (!dtoPreviewToken) {
      setDialogMessage(
        "Missing Microsoft access token. Please close the preview and try again."
      );
      setDialogOpen(true);
      setLoading(false);
      return;
    }

    try {
      const workOrdersCreated: any[] = [];
      const workOrdersRejected: any[] = [];

      console.log("DTO Preview:", dtoPreview);

      if (view?.map?.allLayers) {
        removeAllFromSelectionLayer(view.map.allLayers);
      }

      for (const dtoMap of dtoPreview) {
        console.log("DTO Map:", dtoMap);

        const workOrderResults = await OcpwCreateWorkOrder({
          dtoList: dtoMap,
          token: dtoPreviewToken,
        });

        const results = await Promise.allSettled(
          workOrderResults.map(({ promise }) => promise)
        );

        for (let index = 0; index < results.length; index++) {
          const res = results[index];
          const { dto } = workOrderResults[index];

          if (res.status === "fulfilled") {
            let attachFilesResults: any[] = [];
            let attachLinkResponse: any;

            if (
              dto?.attachFiles &&
              Array.isArray(dto.attachFiles) &&
              dto.attachFiles.length > 0
            ) {
              attachFilesResults = await processAttachFiles({
                proposal: res.value.data.proposal,
                token: dtoPreviewToken,
                files: dto.attachFiles,
              });

              const documentSeriesIds: string[] = [];

              attachFilesResults.forEach((result) => {
                console.log("Attachment upload result:", result);

                if (
                  result.status === "fulfilled" &&
                  result.value &&
                  result.value.status === 200 &&
                  result.value.data?.documentSeriesId &&
                  result.value.data.documentSeriesId.length > 0
                ) {
                  documentSeriesIds.push(result.value.data.documentSeriesId);
                }
              });

              if (documentSeriesIds.length > 0) {
                console.log("List of document series IDs:", documentSeriesIds);

                try {
                  attachLinkResponse = await linkAttachmentToWorkOrder({
                    documentSeriesIds,
                    proposal: res.value.data.proposal,
                    token: dtoPreviewToken,
                  });
                } catch (linkError) {
                  console.error(
                    "Failed to link attachments to work order:",
                    linkError
                  );
                }
              }
            }

            const gisResponse = await updateFeaturesInGisWithWorkOrderResults({
              dto,
              response: res,
            });

            console.log("DTO:", dto);
            console.log("GIS response:", gisResponse);

            if (gisResponse?.updateFeatureResults?.length > 0) {
              const updatedObjectIds = gisResponse.updateFeatureResults
                .map((result: { objectId?: number }) => result.objectId)
                .filter(
                  (objectId: number | undefined): objectId is number =>
                    typeof objectId === "number"
                );

              if (updatedObjectIds.length > 0) {
                let layerId = "";

                if (
                  dto?.features &&
                  Array.isArray(dto.features) &&
                  dto.features.length > 0 &&
                  typeof dto.features[0]?.parentLayerId === "string"
                ) {
                  layerId = dto.features[0].parentLayerId;
                }

                if (layerId.length > 0) {
                  await selectDataHandler(
                    view,
                    {
                      layerId,
                      queryString: `OBJECTID IN (${updatedObjectIds.join(",")})`,
                    },
                    true
                  );
                }
              }
            }

            workOrdersCreated.push({
              dto,
              response: res,
              attachResponse: attachLinkResponse,
              gisResponse: gisResponse || {},
            });
          } else {
            workOrdersRejected.push({
              dto,
              error: res.reason,
            });
          }
        }
      }

      console.log("Work orders created:", workOrdersCreated);
      console.log("Work orders rejected:", workOrdersRejected);

      setCreatedWorkOrders(workOrdersCreated);
      setRejectedWorkOrders(workOrdersRejected);

      setDialogMessage(
        `Work orders:\n ${workOrdersCreated.length} created \n ${workOrdersRejected.length} rejected`
      );

      setDialogOpen(true);
    } catch (createError: any) {
      console.error(createError);

      setDialogMessage(
        getErrorMessage(createError, "An error occurred while creating work orders.")
      );

      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Backdrop sx={{ zIndex: 2000 }} open={true}>
          <CircularProgress size={120} thickness={4} />
        </Backdrop>
      )}

      <Button
        onClick={onClick}
        size="small"
        variant="outlined"
        disabled={
          loading ||
          !payload?.length ||
          inProgress !== InteractionStatus.None
        }
        sx={{ fontSize: 10, height: "40px" }}
      >
        Create work order
      </Button>

      {error && (
        <Dialog
          open={!!error}
          onClose={() => {
            setError(null);
          }}
          aria-labelledby="error-dialog-title"
          aria-describedby="error-dialog-description"
        >
          <DialogTitle id="error-dialog-title" sx={{ color: "error.main" }}>
            Error
          </DialogTitle>

          <DialogContent>
            <Typography id="error-dialog-description">{error}</Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => {
                setError(null);
              }}
              color="primary"
              autoFocus
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <WorkOrderPreviewDialog
        open={dtoPreviewOpen}
        onClose={() => setDtoPreviewOpen(false)}
        onConfirm={handleCreateWorkOrder}
        loading={loading}
        dtoPreview={dtoPreview}
        setDtoPreview={setDtoPreview}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          Work Order Status

          <IconButton
            aria-label="close"
            onClick={() => {
              setDialogOpen(false);
              setWorkOrderSuccessDialogOpen(false);
              setCreatedWorkOrders([]);
              setWorkOrderRejectedDialogOpen(false);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box>{dialogMessage}</Box>
        </DialogContent>

        <DialogActions>
          <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
            <Button
              onClick={() => setWorkOrderSuccessDialogOpen(true)}
              variant="contained"
              sx={{ flex: 1, minWidth: 120, minHeight: 40 }}
            >
              Open created
            </Button>

            <Button
              onClick={() => setWorkOrderRejectedDialogOpen(true)}
              variant="contained"
              sx={{ flex: 1, minWidth: 120, minHeight: 40 }}
            >
              Open rejected
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <WorkOrderDialogSuccessReport
        open={workOrderSuccessDialogOpen}
        onClose={() => setWorkOrderSuccessDialogOpen(false)}
        workOrders={createdWorkOrders}
      />

      <RejectedWorkOrderDialogReport
        open={workOrderRejectedDialogOpen}
        onClose={() => setWorkOrderRejectedDialogOpen(false)}
        rejectedOrders={rejectedWorkOrders}
      />
    </>
  );
};

const processAttachFiles = async ({
  files,
  token,
  proposal,
}: {
  files: File[];
  token: string;
  proposal: any;
}): Promise<any[]> => {
  try {
    if (!files.length) {
      return [];
    }

    const attachPromises: Promise<any>[] = files.map((file) => {
      if (file instanceof File) {
        return uploadAttachmentToAim({
          proposal,
          token,
          file,
        });
      }

      return Promise.reject(new Error("Provided item is not a File instance."));
    });

    const results = await Promise.allSettled(attachPromises);

    return results;
  } catch (attachError: any) {
    if (axios.isAxiosError(attachError) && attachError.response) {
      const err: any = attachError.response.data;
      throw new Error(`Error: ${err.message}`);
    }

    throw new Error(getErrorMessage(attachError, "Failed to attach files."));
  }
};

export default CreateWorkOrder;